import React, { FunctionComponent } from 'react'
import { rgba } from 'polished'

import {
  StyledPageToast,
  StyledPageToastAction,
  StyledPageToastClose,
  StyledPageToastText
} from './ClinPageToast.styles'
import { ClinTheme } from '../../ClinTheme'
import { AnnounceMode } from '../ClinAnnounceBar/ClinAnnounceBar'
import { ClinIcon } from '../ClinIcon'
import { ClinIconPathName } from '../ClinIcon/ClinIcon.paths'
import { ClinText } from '../ClinText'
import { IAnnouncementAction } from '../../types/IAnnouncement'

export interface IClinPageToastProps {
  mode: AnnounceMode
  title?: string
  message?: string
  onClose: () => void
  fullWidth?: boolean
  action?: IAnnouncementAction
}

const toastColour = (mode: AnnounceMode): string => {
  switch (mode) {
    case AnnounceMode.Success:
      return ClinTheme.colors.greenValid
    case AnnounceMode.Error:
      return ClinTheme.colors.redInvalid
    default:
      return ClinTheme.colors.primary
  }
}

/**
 * CLOS-14590: page notifications reserve space below navigation or the checkout
 * header. Errors stay until closed or the page changes.
 */
export const ClinPageToast: FunctionComponent<IClinPageToastProps> = ({
  mode,
  title,
  message,
  onClose,
  fullWidth = false,
  action
}) => {
  const useTint = fullWidth || mode === AnnounceMode.Error

  return (
    <StyledPageToast
      backgroundColor={
        useTint ? rgba(toastColour(mode), 0.15) : toastColour(mode)
      }
      $fullWidth={fullWidth}
      role={mode === AnnounceMode.Error ? 'alert' : 'status'}
      data-testid="clin-page-toast"
    >
      <ClinIcon
        iconSize={ClinTheme.fontSizes[4]}
        iconName={
          mode === AnnounceMode.Success
            ? ClinIconPathName.Success
            : ClinIconPathName.Warning
        }
        iconFill={useTint ? toastColour(mode) : ClinTheme.colors.white}
        aria-hidden
      />
      <StyledPageToastText>
        {title && (
          <ClinText
            as="div"
            fontWeight={ClinTheme.fontWeights.bold}
            fontSize={ClinTheme.fontSizes[1]}
            color={useTint ? ClinTheme.colors.black : ClinTheme.colors.white}
          >
            {title}
          </ClinText>
        )}
        {message && (
          <ClinText
            as="div"
            fontSize={ClinTheme.fontSizes[1]}
            color={useTint ? ClinTheme.colors.black : ClinTheme.colors.white}
          >
            {message}
          </ClinText>
        )}
        {action && (
          <ClinText
            as="div"
            fontSize={ClinTheme.fontSizes[1]}
            color={useTint ? ClinTheme.colors.black : ClinTheme.colors.white}
          >
            <StyledPageToastAction
              type="button"
              onClick={action.onClick}
              data-testid="clin-page-toast-action"
            >
              {action.label}
            </StyledPageToastAction>
          </ClinText>
        )}
      </StyledPageToastText>
      <StyledPageToastClose
        type="button"
        onClick={onClose}
        aria-label="Close notification"
        data-testid="clin-page-toast-close"
      >
        <ClinIcon
          iconName={ClinIconPathName.Cross}
          iconFill={useTint ? toastColour(mode) : ClinTheme.colors.white}
          iconSize={14}
        />
      </StyledPageToastClose>
    </StyledPageToast>
  )
}
